import React from 'react';
import { DateInput, FormCell, NumberInput, Select } from 'ui-toolkit';
import {
  hintTextAmount,
  hintTextDays,
  hintTextPeriod,
  updateBuyerReimbursementComputedFields,
} from '../utilities/commonComputations';
import generatePaymentPeriodOptions from '../utilities/generatePaymentPeriodOptions';
import partsFromDate, { generateDateText } from '../utilities/partsFromDate';
import { createOnChangeHandlerGenerator } from '../utilities/createOnChangeHandlerGenerator';
import { transformDateInputToValue } from '../utilities/transformDateInputToValue';
import { transformLabelValueToValue } from '../utilities/transformLabelValueToValue';
import IHasItemDataProps from './IHasItemDataProps';
import IHasItemEventProps from './IHasItemEventProps';
import IHasMetaDataModalProps from './IHasMetaDataModalProps';
import IHasMetaDataModalState from './IHasMetaDataModalState';
import { IItemWithoutShowSellerProps } from './IItemProps';
import PurchaseItemMetaDataModal, { IPurchaseItemMetaDataModalRef } from './PurchaseItemMetaDataModal';
import SharedPurchaseItemCells from './SharedPurchaseItemCells';
import './_BuyerReimbursement.scss';
import './_ModalPurchasePresentationItem.scss';
import { IBuyerReimbursementToSellerViewItem } from '../../../ui-toolkit/cdItems/interfaces/ICDViewItem';
import { eReimbursements } from '../../../ui-toolkit/enums/eReimbursments';

const labelHash = {
  [eReimbursements.city_and_town_taxes]: 'City/Town Taxes',
  [eReimbursements.county_taxes]: 'CountyTaxes',
  [eReimbursements.assessments]: 'Assessments',
};

const paymentPeriodAsOptions = generatePaymentPeriodOptions();

export interface IBuyerReimbursementToSellerProps
  extends IItemWithoutShowSellerProps,
    IHasItemDataProps<IBuyerReimbursementToSellerViewItem>,
    IHasItemEventProps<IBuyerReimbursementToSellerViewItem>,
    IHasMetaDataModalProps {
  onFocus?: () => void;
  onBlur?: (event: React.FocusEvent) => void;
}

type IBuyerReimbursementToSellerState = IHasMetaDataModalState;

export default class BuyerReimbursementToSeller extends React.Component<
  IBuyerReimbursementToSellerProps,
  IBuyerReimbursementToSellerState
> {
  public state = { expanded: !this.props.disableAutoExpand && this.props.data.new };
  private modal = React.createRef<IPurchaseItemMetaDataModalRef>();
  // private firstInput = React.createRef<Select>();// TODO: add support for focus to Select/Creatable and enable this "autofocus" feature

  public render() {
    const { data, onChange, hasEditPermissions } = this.props;
    const fromDate = partsFromDate(data.date_from);
    const toDate = partsFromDate(data.date_to);
    const period = data.billing_period;
    const selectedPaymentPeriodOption = paymentPeriodAsOptions.find((option) => option.value === period);

    const generateDefaultOnChangeEvent = createOnChangeHandlerGenerator(data, onChange);
    const generateComputedOnChangeEvent = createOnChangeHandlerGenerator(
      data,
      (item: IBuyerReimbursementToSellerViewItem) =>
        onChange(updateBuyerReimbursementComputedFields(item, 'buyer_at_closing'))
    );

    const onBuyerAtClosingOnChangeEvent = generateDefaultOnChangeEvent('buyer_at_closing');
    const periodAmountOnChangeEvent = generateComputedOnChangeEvent('period_amount');
    const fromOnChangeEvent = generateComputedOnChangeEvent('date_from', transformDateInputToValue);
    const toOnChangeEvent = generateComputedOnChangeEvent('date_to', transformDateInputToValue);
    const paymentPeriodOnChangeEvent = generateComputedOnChangeEvent('billing_period', transformLabelValueToValue);

    return (
      <SharedPurchaseItemCells
        data={data}
        buyerAtClosingDisplay="text"
        className="modal-purchase-presentation-item buyer-reimbursement"
        onBlur={this.onBlur}
        onFocus={this.onFocus}
        onChange={onChange}
        showRemove={false}
        hasEditPermissions={hasEditPermissions}
      >
        <PurchaseItemMetaDataModal
          expanded={this.state.expanded}
          label={`${labelHash[data.sub_type]} ${generateDateText(fromDate)} to ${generateDateText(toDate)}`}
          onToggle={this.onModalToggleEvent}
          ref={this.modal}
        >
          <div className="buyer-reimbursement__fields">
            <div>
              <FormCell label={'Billing Period'} hintText={hintTextPeriod(data)}>
                <Select
                  value={selectedPaymentPeriodOption}
                  onChange={paymentPeriodOnChangeEvent}
                  options={paymentPeriodAsOptions}
                  isClearable={true}
                  isDisabled={!hasEditPermissions}
                />
              </FormCell>
            </div>

            <div>
              <NumberInput
                mode="currency"
                label="Billing Amount"
                onBlur={this.onBlur}
                onFocus={this.onFocus}
                onChange={periodAmountOnChangeEvent}
                value={data.period_amount}
                readOnly={!hasEditPermissions}
                debounceOnValueChange
              />
            </div>

            <FormCell hintText={hintTextDays(data)}>
              <DateInput
                label="From Date"
                value={fromDate ? `${fromDate.year}-${fromDate.month}-${fromDate.date}` : null}
                name="date_from"
                onBlur={this.onBlur}
                onChange={fromOnChangeEvent}
                readOnly={!hasEditPermissions}
                attachPortalInsideContainter={true}
              />
            </FormCell>

            <DateInput
              label="To Date"
              value={toDate ? `${toDate.year}-${toDate.month}-${toDate.date}` : null}
              name="date_to"
              onBlur={this.onBlur}
              onChange={toOnChangeEvent}
              readOnly={!hasEditPermissions}
              attachPortalInsideContainter={true}
            />

            <FormCell label="Amount" className="buyer-reimbursement__fields__full-line" hintText={hintTextAmount(data)}>
              <NumberInput
                disabled={!!selectedPaymentPeriodOption}
                mode="currency"
                onBlur={this.onBlur}
                onFocus={this.onFocus}
                onChange={onBuyerAtClosingOnChangeEvent}
                readOnly={!!selectedPaymentPeriodOption || !hasEditPermissions}
                value={data.buyer_at_closing}
              />
            </FormCell>
          </div>
        </PurchaseItemMetaDataModal>
      </SharedPurchaseItemCells>
    );
  }

  private onBlur = (event: React.FocusEvent) => {
    const { onBlur } = this.props;
    const node = this.modal.current;
    if (node && event.relatedTarget && !node.modalContains(event.relatedTarget as HTMLElement)) {
      this.setState({
        expanded: false,
      });
    }
    if (onBlur) {
      onBlur(event);
    }
  };

  private onFocus = () => {
    const { onFocus } = this.props;
    if (onFocus) {
      onFocus();
    }
  };

  private onModalToggleEvent = (expanded: boolean) => this.setState({ expanded });
}
